import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { BILLING_ENABLED } from "@/lib/marketplacePolicy";
import { useState } from "react";
import { MagnifyingGlass, List, X, Plant, User, SignOut } from "@phosphor-icons/react";
import { getUserFirstName, ROLE_LABEL } from "@/lib/format";
import NotificationsBell from "@/components/NotificationsBell";
import NativePushControl from "@/components/NativePushControl";
import { roleHome } from "@/components/RoleGuard";
import { PERSONAL_DASHBOARD_ENABLED } from "@/lib/sniimAlerts";

const links = [
    { to: "/marketplace", label: "Comprar" },
    { to: "/solicitudes", label: "Solicitudes" },
    { to: "/publicar", label: "Publicar" },
    { to: "/directorio/transportista", label: "Transporte" },
];

export default function Navbar() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const firstName = getUserFirstName(user);
    const isAdmin = user?.role === "admin" || user?.roles?.includes("admin");
    const exitAccount = () => {
        const pending = logout();
        setOpen(false);
        nav("/", { replace: true });
        void pending;
    };

    const submitSearch = (e) => {
        e.preventDefault();
        nav(`/marketplace?q=${encodeURIComponent(q)}`);
        setOpen(false);
    };

    return (
        <header className="glass-nav sticky top-0 z-40" data-testid="site-header">
            <div className="container-page flex items-center gap-3 h-14 sm:h-16 overflow-visible">
                <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-link">
                    <span className="w-9 h-9 rounded-lg bg-[#2E7D32] text-white grid place-items-center">
                        <Plant weight="fill" size={22} />
                    </span>
                    <div className="leading-tight">
                        <div className="font-display font-extrabold text-lg tracking-tight">CampoDirecto</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground -mt-0.5">México</div>
                    </div>
                </Link>

                <form onSubmit={submitSearch} className="hidden md:flex flex-1 min-w-0 max-w-sm relative" data-testid="global-search-form">
                    <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Busca becerros, alfalfa, tractores…"
                        className="w-full h-11 pl-9 pr-4 rounded-full border border-border bg-white/90 focus:outline-none focus:border-[#2E7D32]"
                        data-testid="global-search-input"
                    />
                </form>

                <nav className="hidden xl:flex items-center gap-0.5 whitespace-nowrap" aria-label="Navegación principal">
                    {links.map((l) => (
                        <NavLink key={l.to} to={l.to}
                            className={({ isActive }) => `px-2.5 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${isActive ? "text-[#2E7D32] bg-[#2E7D32]/8" : "text-foreground hover:text-[#2E7D32]"}`}
                            data-testid={`nav-${l.to.replaceAll('/', '-')}`}>
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="ml-auto flex items-center gap-2">
                    {user && <NativePushControl user={user} />}
                    {user ? (
                        <div className="hidden sm:flex items-center gap-2">
                            <NotificationsBell user={user} />
                            {PERSONAL_DASHBOARD_ENABLED && <Link to="/mi-mercado" className="hidden lg:inline-flex h-10 items-center px-3 rounded-full border border-border text-sm font-semibold hover:border-[#2E7D32] hover:text-[#2E7D32]">
                                Mi mercado
                            </Link>}
                            <Link to={roleHome(user.roles?.includes("admin") ? "admin" : user.role)} className="btn-outline h-10 px-3 text-sm whitespace-nowrap" data-testid="dashboard-link">
                                <User size={16} weight="bold" />
                                {firstName && <span className="ml-1">{firstName}</span>}
                                <span className="ml-2 text-[10px] uppercase tracking-widest text-[#2E7D32] font-bold hidden md:inline" data-testid="navbar-role-label">
                                    {(user.role === "admin" || user.roles?.includes("admin")) ? "ADMIN" : "MI CUENTA"}
                                </span>
                            </Link>
                            <button onClick={async () => { await logout(); nav("/"); }} className="p-2 rounded-full hover:bg-secondary" data-testid="logout-button" title="Cerrar sesión">
                                <SignOut size={18} />
                            </button>
                        </div>
                    ) : (
                        <div className="hidden sm:flex items-center gap-2">
                            <Link to="/ingresar" className="btn-outline h-10 px-3 text-sm whitespace-nowrap" data-testid="login-link">Ingresar</Link>
                            {BILLING_ENABLED ? (
                                <Link to="/planes" className="btn-primary h-10 px-4 text-sm" data-testid="plans-link">Ver planes</Link>
                            ) : (
                                <Link to="/registro" className="btn-primary h-10 px-3 text-sm whitespace-nowrap" data-testid="register-link">Crear cuenta</Link>
                            )}
                        </div>
                    )}

                    {user && isAdmin && (
                        <button type="button" onClick={exitAccount}
                            className="sm:hidden inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-red-200 text-red-700"
                            aria-label="Cerrar sesión de administrador" title="Cerrar sesión"
                            data-testid="admin-global-mobile-signout">
                            <SignOut size={21} weight="bold" aria-hidden="true" />
                        </button>
                    )}

                    <button className="hidden md:inline-flex xl:hidden p-2 rounded-full hover:bg-secondary" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menú" aria-expanded={open} aria-controls="mobile-navigation">
                        {open ? <X size={22} /> : <List size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div id="mobile-navigation" className="xl:hidden border-t border-border bg-white/95" data-testid="mobile-menu">
                    <div className="container-page py-4 flex flex-col gap-1">
                        <form onSubmit={submitSearch} className="relative mb-2">
                            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…"
                                className="w-full h-11 pl-9 pr-4 rounded-full border border-border" data-testid="mobile-search-input" />
                        </form>
                        {links.map((l) => (
                            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)}
                                className="px-3 py-3 rounded-lg text-base font-semibold hover:bg-secondary">{l.label}</NavLink>
                        ))}
                        <div className="h-px bg-border my-2" />
                        {user ? (
                            <>
                                {PERSONAL_DASHBOARD_ENABLED && <Link to="/mi-mercado" onClick={() => setOpen(false)} className="px-3 py-3 rounded-lg text-base font-semibold hover:bg-secondary">Mi cuenta</Link>}
                                <Link to={roleHome(user.roles?.includes("admin") ? "admin" : user.role)} onClick={() => setOpen(false)} className="btn-outline h-11">Mi panel</Link>
                                <button onClick={exitAccount} className="btn-outline h-11 mt-2">Cerrar sesión</button>
                            </>
                        ) : (
                            <>
                                <Link to="/ingresar" onClick={() => setOpen(false)} className="btn-outline h-11">Ingresar</Link>
                                <Link to="/registro" onClick={() => setOpen(false)} className="btn-primary h-11 mt-2">Crear cuenta</Link>
                            </>
                        )}
                        {user?.role === "productor" || user?.role === "proveedor" || user?.role === "exportador" ? (
                            <Link to="/publicar" onClick={() => setOpen(false)} className="btn-primary h-11 mt-2" data-testid="publish-cta-mobile">Publicar producto</Link>
                        ) : null}
                    </div>
                </div>
            )}
        </header>
    );
}
